// ── ZoneMessageBus — typed server→zone event bridge (SMA-63) ──
//
// Framework-agnostic pub/sub bus that the coordinator publishes pre-parsed
// server payloads to, and the active painted zone subscribes to.
// No Phaser or Colyseus imports — only shared payload types.

import {
  MSG_ENTITY_SPAWN,
  MSG_ENTITY_DESPAWN,
  MSG_ENEMY_WINDUP,
  MSG_PLAYER_FAINT,
  MSG_TUTORIAL_BEAT,
  MSG_TUTORIAL_NPC_SPAWN,
  MSG_TUTORIAL_NPC_DESPAWN,
  MSG_TIDE_UPDATE,
  MSG_BELL_RING,
  MSG_PLAYER_POSITION,
  MSG_ENTITIES_TICK,
  MSG_NPC_SPAWN,
  MSG_NPC_DIALOGUE,
  MSG_LOOT_SPAWN,
  MSG_LOOT_DESPAWN,
  MSG_LOOT_PICKUP,
  MSG_GATHER_NODE_SPAWN,
  MSG_GATHER_NODE_COOLDOWN,
  MSG_GATHER_COMPLETE,
  MSG_GATHER_CANCELLED,
  MSG_WEATHER_CHANGE,
  MSG_SKY_STATE,
  MSG_PLAYER_REALM_CHANGE,
  MSG_REALM_FIRST_VISIT,
  MSG_ZONE_ENTER,
  MSG_ZONE_LEAVE,
  MSG_ZONE_GRAPH,
  MSG_FIRE_SAT,
  MSG_FIRE_STOOD,
  MSG_MARSHMALLOW_ROAST_TICK,
  MSG_MARSHMALLOW_ROAST_COMPLETE,
  MSG_FIRE_EMOTE_PLAYED,
  MSG_AUTO_TRAVEL_STATE,
  MSG_AUTO_TRAVEL_DESTINATIONS,
  MSG_BUILD_STATE,
  MSG_BUILD_RESULT,
  MSG_BUILD_BLOCK_PLACED,
  MSG_BUILD_BLOCK_REMOVED,
  MSG_BUILD_CURRENCY_SYNC,
} from '@game/shared';

import type {
  EntitySpawnEvent,
  EntityDespawnEvent,
  EnemyWindupEvent,
  PlayerFaintEvent,
  TutorialBeatEvent,
  TutorialNpcSpawnEvent,
  TutorialNpcDespawnEvent,
  TideUpdateEvent,
  BellRingEvent,
  PlayerPositionEvent,
  EntitiesTickEvent,
  NpcSpawnEvent,
  NpcDialogueEvent,
  LootSpawnEvent,
  LootDespawnEvent,
  LootPickupEvent,
  GatherNodeSpawnEvent,
  GatherNodeCooldownEvent,
  GatherCompleteResult,
  GatherCancelledEvent,
  WeatherChangePayload,
  SkyStatePayload,
  PlayerRealmChangeEvent,
  RealmFirstVisitEvent,
  ZoneEnterEvent,
  ZoneLeaveEvent,
  ZoneGraphPayload,
  FireSatEvent,
  FireStoodEvent,
  MarshmallowRoastTickEvent,
  MarshmallowRoastCompleteEvent,
  FireEmotePlayedEvent,
  AutoTravelStateEvent,
  AutoTravelDestinationsEvent,
  BuildStateEvent,
  BuildResultEvent,
  BuildBlockPlacedEvent,
  BuildBlockRemovedEvent,
  BuildCurrencySyncEvent,
} from '@game/shared';

// ── Payload map: MSG constant → typed payload ──

export interface ZoneMessageMap {
  [MSG_PLAYER_POSITION]: PlayerPositionEvent;
  [MSG_ENTITIES_TICK]: EntitiesTickEvent;
  [MSG_ENTITY_SPAWN]: EntitySpawnEvent;
  [MSG_ENTITY_DESPAWN]: EntityDespawnEvent;
  [MSG_ENEMY_WINDUP]: EnemyWindupEvent;
  [MSG_PLAYER_FAINT]: PlayerFaintEvent;

  [MSG_NPC_SPAWN]: NpcSpawnEvent;
  [MSG_NPC_DIALOGUE]: NpcDialogueEvent;
  [MSG_TUTORIAL_BEAT]: TutorialBeatEvent;
  [MSG_TUTORIAL_NPC_SPAWN]: TutorialNpcSpawnEvent;
  [MSG_TUTORIAL_NPC_DESPAWN]: TutorialNpcDespawnEvent;

  [MSG_GATHER_NODE_SPAWN]: GatherNodeSpawnEvent;
  [MSG_GATHER_NODE_COOLDOWN]: GatherNodeCooldownEvent;
  [MSG_GATHER_COMPLETE]: GatherCompleteResult;
  [MSG_GATHER_CANCELLED]: GatherCancelledEvent;

  [MSG_LOOT_SPAWN]: LootSpawnEvent;
  [MSG_LOOT_DESPAWN]: LootDespawnEvent;
  [MSG_LOOT_PICKUP]: LootPickupEvent;

  [MSG_WEATHER_CHANGE]: WeatherChangePayload;
  [MSG_SKY_STATE]: SkyStatePayload;

  [MSG_PLAYER_REALM_CHANGE]: PlayerRealmChangeEvent;
  [MSG_REALM_FIRST_VISIT]: RealmFirstVisitEvent;
  [MSG_ZONE_ENTER]: ZoneEnterEvent;
  [MSG_ZONE_LEAVE]: ZoneLeaveEvent;
  [MSG_ZONE_GRAPH]: ZoneGraphPayload;

  [MSG_FIRE_SAT]: FireSatEvent;
  [MSG_FIRE_STOOD]: FireStoodEvent;
  [MSG_MARSHMALLOW_ROAST_TICK]: MarshmallowRoastTickEvent;
  [MSG_MARSHMALLOW_ROAST_COMPLETE]: MarshmallowRoastCompleteEvent;
  [MSG_FIRE_EMOTE_PLAYED]: FireEmotePlayedEvent;

  [MSG_TIDE_UPDATE]: TideUpdateEvent;
  [MSG_BELL_RING]: BellRingEvent;

  [MSG_AUTO_TRAVEL_STATE]: AutoTravelStateEvent;
  [MSG_AUTO_TRAVEL_DESTINATIONS]: AutoTravelDestinationsEvent;

  [MSG_BUILD_STATE]: BuildStateEvent;
  [MSG_BUILD_RESULT]: BuildResultEvent;
  [MSG_BUILD_BLOCK_PLACED]: BuildBlockPlacedEvent;
  [MSG_BUILD_BLOCK_REMOVED]: BuildBlockRemovedEvent;
  [MSG_BUILD_CURRENCY_SYNC]: BuildCurrencySyncEvent;
}

export type ZoneMessageType = keyof ZoneMessageMap;

export type ZoneMessageHandler<T extends ZoneMessageType> = (payload: ZoneMessageMap[T]) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- internal storage erases payload type; type safety enforced at subscribe/publish boundaries
type AnyHandler = (payload: any) => void;

export class ZoneMessageBus {
  private listeners = new Map<ZoneMessageType, Set<AnyHandler>>();

  subscribe<T extends ZoneMessageType>(type: T, handler: ZoneMessageHandler<T>): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    const fn: AnyHandler = handler;
    set.add(fn);
    return () => { set!.delete(fn); };
  }

  subscribeMany<T extends ZoneMessageType>(entries: { type: T; handler: ZoneMessageHandler<T> }[]): () => void {
    const unsubs = entries.map(({ type, handler }) => this.subscribe(type, handler));
    return () => { for (const u of unsubs) u(); };
  }

  publish<T extends ZoneMessageType>(type: T, payload: ZoneMessageMap[T]): void {
    const set = this.listeners.get(type);
    if (!set) return;
    for (const handler of set) {
      handler(payload);
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}
